/** Sortable drag-and-drop row for a configured AI provider */

import { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, ChevronDown, ChevronUp } from 'lucide-react'
import type { AIProviderSetting } from '@agentwiki/shared'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { badgeClass, buttonDangerClass, buttonPrimaryClass, cardClass, iconButtonClass, inputClass, labelClass } from '../ui/styles'

interface SortableProviderRowProps {
  setting: AIProviderSetting
  providerName: string
  models: string[]
  onSave: (apiKey: string, model: string, enabled: boolean) => void
  onDelete: () => void
  isSaving: boolean
}

export function SortableProviderRow({ setting, providerName, models, onSave, onDelete, isSaving }: SortableProviderRowProps) {
  const t = useT()
  const [expanded, setExpanded] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [model, setModel] = useState(setting.defaultModel || models[0])
  const [enabled, setEnabled] = useState(setting.isEnabled)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: setting.providerId })

  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }
  const idBase = `provider-${setting.providerId}`

  return (
    <div ref={setNodeRef} style={style} className={cardClass}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className={cn(iconButtonClass, 'cursor-grab touch-none')}
          aria-label={t('settings.ai.dragToReorder', { name: providerName })}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-fg">{providerName}</span>
        <span
          className={cn(
            'rounded-md px-2 py-0.5 text-xs',
            setting.isEnabled ? 'bg-success-soft text-success' : 'bg-surface-muted text-fg-subtle',
          )}
        >
          {setting.isEnabled ? t('settings.ai.active') : t('settings.ai.disabled')}
        </span>
        <span className={cn(badgeClass, 'hidden font-mono sm:inline-flex')}>{setting.defaultModel}</span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={iconButtonClass}
          aria-expanded={expanded}
          aria-label={expanded ? t('settings.ai.collapse', { name: providerName }) : t('settings.ai.expand', { name: providerName })}
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {expanded && (
        <div className="space-y-3 border-t border-border p-3">
          <div className="space-y-1.5">
            <label htmlFor={`${idBase}-key`} className={labelClass}>
              {t('settings.ai.apiKey')}
            </label>
            <input
              id={`${idBase}-key`}
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={t('settings.ai.keySaved')}
              className={inputClass}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor={`${idBase}-model`} className={labelClass}>
              {t('settings.ai.model')}
            </label>
            <select id={`${idBase}-model`} value={model} onChange={(e) => setModel(e.target.value)} className={inputClass}>
              {models.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <label className="flex min-h-8 cursor-pointer items-center gap-2 text-sm text-fg-muted">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 rounded accent-accent" />
            {t('settings.ai.enabled')}
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                onSave(apiKey || '__unchanged__', model, enabled)
                setApiKey('')
                setExpanded(false)
              }}
              disabled={isSaving}
              className={buttonPrimaryClass}
            >
              {isSaving ? t('settings.saving') : t('settings.save')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm(t('settings.ai.removeConfirm', { name: providerName }))) onDelete()
              }}
              className={buttonDangerClass}
            >
              {t('settings.remove')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
